// Perigon API service for news summarization and search
// API Documentation: https://docs.goperigon.com/

const PERIGON_API_KEY = '58a72bff-3e43-4c99-90a1-c942f50a6fed';
const PERIGON_BASE_URL = 'https://api.goperigon.com/v1';

export interface PerigonArticle {
  articleId: string;
  title: string;
  description: string;
  content: string;
  url: string;
  imageUrl?: string;
  pubDate: string;
  addDate: string;
  source: {
    domain: string;
    title?: string;
  };
  authors?: string[];
  labels?: string[];
  categories?: string[];
  topics?: string[];
  sentiment?: {
    positive: number;
    neutral: number;
    negative: number;
  };
}

export interface PerigonSummaryResponse {
  summary: string;
  articles: PerigonArticle[];
  numResults: number;
  metadata?: {
    model: string;
    method: string;
  };
}

export interface PerigonSearchParams {
  q?: string;
  title?: string;
  description?: string;
  content?: string;
  from?: string;
  to?: string;
  sortBy?: 'relevance' | 'date' | 'reverseDate';
  page?: number;
  size?: number;
  category?: string[];
  topic?: string[];
  language?: string[];
  sourceGroup?: string[];
  excludeLabel?: string[];
  showNumResults?: boolean;
}

export interface PerigonSummarizeParams extends PerigonSearchParams {
  // Summarization params removed - using standard endpoint
}

/**
 * Fetch news articles from Perigon API
 */
export const fetchPerigonNewsSummary = async (
  params: PerigonSummarizeParams = {}
): Promise<PerigonSummaryResponse> => {
  const defaultParams: PerigonSearchParams = {
    sortBy: 'date',
    size: 10,
    language: ['en'],
    excludeLabel: ['Opinion', 'Paid-news', 'Press Release'],
    showNumResults: true,
    from: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Last 7 days
    ...params,
  };

  const queryParams = new URLSearchParams();
  
  // Add search filters as query parameters
  if (defaultParams.q) queryParams.append('q', defaultParams.q);
  if (defaultParams.from) queryParams.append('from', defaultParams.from);
  if (defaultParams.to) queryParams.append('to', defaultParams.to);
  if (defaultParams.sortBy) queryParams.append('sortBy', defaultParams.sortBy);
  if (defaultParams.page !== undefined) queryParams.append('page', defaultParams.page.toString());
  if (defaultParams.size) queryParams.append('size', defaultParams.size.toString());
  if (defaultParams.showNumResults !== undefined) queryParams.append('showNumResults', defaultParams.showNumResults.toString());
  
  if (defaultParams.category) {
    defaultParams.category.forEach(cat => queryParams.append('category', cat));
  }
  if (defaultParams.language) {
    defaultParams.language.forEach(lang => queryParams.append('language', lang));
  }
  if (defaultParams.sourceGroup) {
    defaultParams.sourceGroup.forEach(sg => queryParams.append('sourceGroup', sg));
  }
  if (defaultParams.excludeLabel) {
    defaultParams.excludeLabel.forEach(label => queryParams.append('excludeLabel', label));
  }

  try {
    const response = await fetch(`${PERIGON_BASE_URL}/all?${queryParams.toString()}`, {
      method: 'GET',
      headers: {
        'x-api-key': PERIGON_API_KEY,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Perigon API error: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    
    // Transform response to match expected format
    return {
      summary: '', // No AI summary from standard endpoint
      articles: data.articles || [],
      numResults: data.numResults || 0,
    };
  } catch (error) {
    console.error('Error fetching Perigon news summary:', error);
    throw error;
  }
};

/**
 * Fetch trending news with default parameters
 */
export const fetchTrendingNews = async (): Promise<PerigonSummaryResponse> => {
  return fetchPerigonNewsSummary({
    sortBy: 'date',
    size: 9,
    category: ['Tech', 'Business', 'Science', 'Politics'],
    sourceGroup: ['top100'],
    from: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Last 2 days
  });
};

/**
 * Fetch news by specific topic
 */
export const fetchNewsByTopic = async (topic: string): Promise<PerigonSummaryResponse> => {
  return fetchPerigonNewsSummary({
    q: topic,
    sortBy: 'relevance',
    size: 10,
    from: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  });
};

/**
 * Fetch news by category
 */
export const fetchNewsByCategory = async (category: string): Promise<PerigonSummaryResponse> => {
  return fetchPerigonNewsSummary({
    category: [category],
    sortBy: 'date',
    size: 10,
    from: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  });
};
